import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  Form,
  InputNumber,
  Modal,
  Pagination,
  Result,
  Select,
  Spin,
  Switch,
  Table,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import ReactEcharts from 'echarts-for-react';
import { toast } from 'react-toastify';
import aiTokenUsageService, {
  IModelPricing,
  ITokenCounts,
  ITokenSummary,
  ITokenUsageLog,
} from '../../../services/aiChat/aiTokenUsage.service';

const { Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const PAGE_SIZE = 50;
const SERVICE_OPTIONS = ['chat-ui', 'sn-normalization', 'sn-rule-synthesis'];

// Exact color palette from token-dashboard
const C = {
  input:  '#4A9EFF',
  output: '#7C5CFF',
  cost:   '#3FB68B',
  cache:  '#0EA5A4',
  cacheWrite: '#7DD3D2',
  warn:   '#E8A23B',
  border: '#e5e7eb',
  muted:  '#6b7280',
  muted2: '#9ca3af',
  text:   '#111827',
  surface:'#f9fafb',
};
const MONO = "'JetBrains Mono','Courier New',monospace";

// ─── Shared inline styles ─────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: '#fff', border: `1px solid ${C.border}`,
  borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const cardPad: React.CSSProperties = { ...card, padding: '18px 20px' };
const kpiLabel: React.CSSProperties = {
  color: C.muted, textTransform: 'uppercase',
  fontSize: 10, letterSpacing: '0.08em', fontWeight: 600, marginBottom: 6,
};
const kpiSub: React.CSSProperties = { color: C.muted2, fontSize: 11, marginTop: 3 };
const sectionTitle: React.CSSProperties = {
  margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: C.text,
};

// ─── Formatting helpers ───────────────────────────────────────────────────────
const fmt = {
  compact: (n: number) =>
    n >= 1_000_000_000 ? `${(n / 1_000_000_000).toFixed(2)}B`
    : n >= 1_000_000   ? `${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000       ? `${(n / 1_000).toFixed(1)}K`
    : String(n),
  usd: (n: number) => `$${n.toFixed(4)}`,
  dt:  (iso: string) => new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }),
};

// ─── Token / cost helpers ─────────────────────────────────────────────────────
// input_tokens is total input (cache included); cache_read/cache_write are portions of it.
const cacheRead  = (t: ITokenCounts) => Number(t.cache_read_tokens ?? 0);
const cacheWrite = (t: ITokenCounts) => Number(t.cache_write_tokens ?? 0);
const uncachedInput = (t: ITokenCounts) => Math.max(0, t.input_tokens - cacheRead(t) - cacheWrite(t));
const cacheHitPct = (t: ITokenCounts) => (t.input_tokens > 0 ? (cacheRead(t) / t.input_tokens) * 100 : 0);
const hasRate = (v: number | null | undefined) => v !== null && v !== undefined;
const fmtPct = (p: number) => (p > 0 && p < 1 ? '<1%' : `${p.toFixed(0)}%`);

// Cached tokens use the model's cache rates; a rate that isn't configured falls back to the input rate.
function estimateCost(t: ITokenCounts, pricing: IModelPricing[], model?: string): number {
  const p = pricing.find(pr => pr.model === model);
  if (!p) return 0;
  const inRate = Number(p.input_cost_per_1m);
  const readRate  = hasRate(p.cache_read_cost_per_1m)  ? Number(p.cache_read_cost_per_1m)  : inRate;
  const writeRate = hasRate(p.cache_write_cost_per_1m) ? Number(p.cache_write_cost_per_1m) : inRate;
  return (uncachedInput(t) / 1_000_000) * inRate +
    (cacheRead(t)  / 1_000_000) * readRate +
    (cacheWrite(t) / 1_000_000) * writeRate +
    (t.output_tokens / 1_000_000) * Number(p.output_cost_per_1m);
}
function totalCost(summary: ITokenSummary, pricing: IModelPricing[]): number {
  return summary.by_model.reduce((s, m) => s + estimateCost(m, pricing, m.model), 0);
}

// Cached-input cell: cache reads, with the write count and hit rate on hover.
const CachedCell: React.FC<{ t: ITokenCounts; compact?: boolean }> = ({ t, compact }) => {
  const read = cacheRead(t);
  if (!read && !cacheWrite(t)) return <Text type="secondary">—</Text>;
  return (
    <Tooltip title={`Cache read: ${read.toLocaleString()} · Cache write: ${cacheWrite(t).toLocaleString()} · ${cacheHitPct(t).toFixed(0)}% of input from cache`}>
      <span style={{ fontFamily: MONO, color: C.cache }}>{compact ? fmt.compact(read) : read.toLocaleString()}</span>
    </Tooltip>
  );
};

// ─── Service badge ────────────────────────────────────────────────────────────
const serviceBadge = (service: string) => {
  const MAP: Record<string, { bg: string; color: string }> = {
    'chat-ui':           { bg: 'rgba(74,158,255,0.1)',  color: C.input  },
    'sn-normalization':  { bg: 'rgba(124,92,255,0.1)',  color: C.output },
    'sn-rule-synthesis': { bg: 'rgba(232,162,59,0.1)',  color: C.warn   },
  };
  const s = MAP[service] ?? { bg: C.surface, color: C.muted };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 7px', borderRadius: 4,
      fontSize: 11, fontWeight: 500, fontFamily: MONO,
      background: s.bg, color: s.color, border: `1px solid ${s.color}33`,
    }}>
      {service}
    </span>
  );
};

// ─── KPI card ─────────────────────────────────────────────────────────────────
interface KpiProps { label: string; value: string; sub?: string; color?: string }
const Kpi: React.FC<KpiProps> = ({ label, value, sub, color = C.text }) => (
  <div style={cardPad}>
    <div style={kpiLabel}>{label}</div>
    <div style={{ fontFamily: MONO, fontSize: 22, fontWeight: 500, letterSpacing: '-0.03em', lineHeight: 1.1, color }}>
      {value}
    </div>
    {sub && <div style={kpiSub}>{sub}</div>}
  </div>
);

// ─── Input split into uncached / cache write / cache read (stacked segments) ──
const INPUT_PARTS = ['Input (uncached)', 'Cache write', 'Cache read'];
function inputSeries(rows: ITokenCounts[], stack: string, barMaxWidth?: number) {
  return [
    { name: INPUT_PARTS[0], type: 'bar', stack, data: rows.map(uncachedInput), itemStyle: { color: C.input },      barMaxWidth },
    { name: INPUT_PARTS[1], type: 'bar', stack, data: rows.map(cacheWrite),    itemStyle: { color: C.cacheWrite }, barMaxWidth },
    { name: INPUT_PARTS[2], type: 'bar', stack, data: rows.map(cacheRead),     itemStyle: { color: C.cache },      barMaxWidth },
  ];
}

// ─── Daily stacked bar ────────────────────────────────────────────────────────
function DailyChart({ daily }: { daily: ITokenSummary['daily'] }) {
  return <ReactEcharts style={{ height: 240 }} option={{
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: [...INPUT_PARTS, 'Output'], bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 40, right: 12, top: 8, bottom: 36, containLabel: true },
    xAxis: {
      type: 'category', data: daily.map(d => d.date),
      axisLabel: { rotate: daily.length > 14 ? 30 : 0, fontSize: 10 }, axisTick: { show: false },
    },
    yAxis: { type: 'value', axisLabel: { formatter: (v: number) => fmt.compact(v), fontSize: 10 } },
    series: [
      ...inputSeries(daily, 'total', 32),
      { name: 'Output', type: 'bar', stack: 'total', data: daily.map(d => d.output_tokens), itemStyle: { color: C.output }, barMaxWidth: 32 },
    ],
  }} />;
}

// ─── Model donut ──────────────────────────────────────────────────────────────
function ModelDonut({ byModel }: { byModel: ITokenSummary['by_model'] }) {
  const COLORS = [C.input, C.output, C.cost, C.warn, '#E5484D'];
  return <ReactEcharts style={{ height: 240 }} option={{
    tooltip: { trigger: 'item', formatter: (p: any) => `${p.name}: ${fmt.compact(p.value)} (${p.percent}%)` },
    legend: { orient: 'vertical', right: 0, top: 'center', textStyle: { fontSize: 11 } },
    series: [{
      type: 'pie', radius: ['40%', '66%'], center: ['36%', '50%'],
      data: byModel.map((m, i) => ({
        name: m.model.replace('claude-', '').replace('-20251001', ''),
        value: m.input_tokens + m.output_tokens,
        itemStyle: { color: COLORS[i % COLORS.length] },
      })),
      label: { show: false }, emphasis: { label: { show: false } },
    }],
  }} />;
}

// ─── By-service grouped bar ───────────────────────────────────────────────────
function ServiceGroupedBar({ byService }: { byService: ITokenSummary['by_service'] }) {
  return <ReactEcharts style={{ height: 240 }} option={{
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      formatter: (p: any[]) => {
        const s = byService[p[0]?.dataIndex ?? 0];
        return `${s.service}<br/>Calls: ${s.call_count}<br/>Input: ${fmt.compact(s.input_tokens)}` +
          ` (uncached ${fmt.compact(uncachedInput(s))}, cache write ${fmt.compact(cacheWrite(s))}, cache read ${fmt.compact(cacheRead(s))})` +
          `<br/>Output: ${fmt.compact(s.output_tokens)}`;
      },
    },
    legend: { data: [...INPUT_PARTS, 'Output'], bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 12, right: 12, top: 8, bottom: 36, containLabel: true },
    xAxis: { type: 'category', data: byService.map(s => s.service), axisLabel: { fontSize: 10 } },
    yAxis: { type: 'value', axisLabel: { formatter: (v: number) => fmt.compact(v), fontSize: 10 } },
    series: [
      ...inputSeries(byService, 'input', 36),
      { name: 'Output', type: 'bar', data: byService.map(s => s.output_tokens), itemStyle: { color: C.output }, barMaxWidth: 36 },
    ],
  }} />;
}

// ─── Model horizontal bar ─────────────────────────────────────────────────────
function ModelBar({ byModel, pricing }: { byModel: ITokenSummary['by_model']; pricing: IModelPricing[] }) {
  return <ReactEcharts style={{ height: Math.max(160, byModel.length * 48 + 40) }} option={{
    tooltip: {
      trigger: 'axis', axisPointer: { type: 'shadow' },
      formatter: (p: any[]) => {
        const m = byModel[p[0]?.dataIndex ?? 0];
        const cost = estimateCost(m, pricing, m.model);
        return `${m.model}<br/>Input: ${fmt.compact(m.input_tokens)} (cached ${fmt.compact(cacheRead(m))})<br/>Output: ${fmt.compact(m.output_tokens)}<br/>Est. cost: ${fmt.usd(cost)}`;
      },
    },
    grid: { left: 12, right: 12, top: 8, bottom: 12, containLabel: true },
    xAxis: { type: 'value', axisLabel: { formatter: (v: number) => fmt.compact(v), fontSize: 10 } },
    yAxis: { type: 'category', data: byModel.map(m => m.model.replace('claude-', '').replace('-20251001', '')), axisLabel: { fontSize: 10 } },
    series: [
      ...inputSeries(byModel, 'total'),
      { name: 'Output', type: 'bar', stack: 'total', data: byModel.map(m => m.output_tokens), itemStyle: { color: C.output } },
    ],
    legend: { data: [...INPUT_PARTS, 'Output'], bottom: 0, textStyle: { fontSize: 11 } },
  }} />;
}

// ─── Service bar chart (By Service tab) ───────────────────────────────────────
function ServiceBar({ byService }: { byService: ITokenSummary['by_service'] }) {
  return <ReactEcharts style={{ height: 240 }} option={{
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: [...INPUT_PARTS, 'Output'], bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 12, right: 12, top: 8, bottom: 36, containLabel: true },
    xAxis: { type: 'category', data: byService.map(s => s.service), axisLabel: { fontSize: 11 } },
    yAxis: { type: 'value', axisLabel: { formatter: (v: number) => fmt.compact(v) } },
    series: [
      ...inputSeries(byService, 'total', 48),
      { name: 'Output', type: 'bar', stack: 'total', data: byService.map(s => s.output_tokens), itemStyle: { color: C.output }, barMaxWidth: 48 },
    ],
  }} />;
}

// ─── Edit Pricing Modal ───────────────────────────────────────────────────────
const EditPricingModal: React.FC<{
  record: IModelPricing | null; onCancel: () => void; onSuccess: () => void;
}> = ({ record, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) form.setFieldsValue({
      input_cost_per_1m: Number(record.input_cost_per_1m),
      output_cost_per_1m: Number(record.output_cost_per_1m),
      cache_read_cost_per_1m:  hasRate(record.cache_read_cost_per_1m)  ? Number(record.cache_read_cost_per_1m)  : null,
      cache_write_cost_per_1m: hasRate(record.cache_write_cost_per_1m) ? Number(record.cache_write_cost_per_1m) : null,
      is_active: record.is_active,
    });
  }, [record, form]);

  const onFinish = async (values: any) => {
    if (!record) return;
    setSaving(true);
    try {
      // A cleared cache field is sent as null (not configured → priced at the input rate).
      await aiTokenUsageService.upsertPricing(
        record.model, values.input_cost_per_1m, values.output_cost_per_1m, values.is_active,
        values.cache_read_cost_per_1m ?? null, values.cache_write_cost_per_1m ?? null,
      );
      toast.success('Pricing updated');
      onSuccess();
    } catch { toast.error('Failed to update pricing'); }
    finally { setSaving(false); }
  };

  return (
    <Modal wrapClassName="custom-modal" title={`Edit Pricing — ${record?.model ?? ''}`}
      centered visible={!!record} onCancel={onCancel} footer={null} destroyOnClose width={460}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item label="Input cost per 1M tokens (USD)" name="input_cost_per_1m" rules={[{ required: true }]}>
          <InputNumber min={0} step={0.1} precision={6} style={{ width: '100%' }} prefix="$" />
        </Form.Item>
        <Form.Item label="Output cost per 1M tokens (USD)" name="output_cost_per_1m" rules={[{ required: true }]}>
          <InputNumber min={0} step={0.1} precision={6} style={{ width: '100%' }} prefix="$" />
        </Form.Item>
        <Form.Item label="Cache read cost per 1M tokens (USD)" name="cache_read_cost_per_1m"
          extra="Usually 0.1× the input rate. Leave blank to price cache reads at the input rate.">
          <InputNumber min={0} step={0.01} precision={6} style={{ width: '100%' }} prefix="$" />
        </Form.Item>
        <Form.Item label="Cache write cost per 1M tokens (USD)" name="cache_write_cost_per_1m"
          extra="Usually 1.25× the input rate (5-minute cache). Leave blank to price cache writes at the input rate.">
          <InputNumber min={0} step={0.01} precision={6} style={{ width: '100%' }} prefix="$" />
        </Form.Item>
        <Form.Item label="Active" name="is_active" valuePropName="checked"><Switch /></Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
        </div>
      </Form>
    </Modal>
  );
};

// ─── Tips builder ─────────────────────────────────────────────────────────────
function buildTips(summary: ITokenSummary, pricing: IModelPricing[]) {
  const tips: { type: 'warning'|'info'|'success'; title: string; description: string }[] = [];
  const totalTok = summary.totals.input_tokens + summary.totals.output_tokens;

  const unpriced = summary.by_model.map(m => m.model)
    .filter(m => !pricing.filter(p => p.is_active).map(p => p.model).includes(m));
  if (unpriced.length) tips.push({ type: 'warning',
    title: 'Missing pricing configuration',
    description: `${unpriced.join(', ')} ${unpriced.length === 1 ? 'has' : 'have'} no active pricing. Cost estimates are incomplete. Add pricing in the Pricing Config tab.`,
  });

  const noCacheRates = summary.by_model
    .filter(m => cacheRead(m) + cacheWrite(m) > 0)
    .filter(m => {
      const p = pricing.find(pr => pr.model === m.model && pr.is_active);
      return p && (!hasRate(p.cache_read_cost_per_1m) || !hasRate(p.cache_write_cost_per_1m));
    })
    .map(m => m.model);
  if (noCacheRates.length) tips.push({ type: 'warning',
    title: 'Cache pricing not configured',
    description: `${noCacheRates.join(', ')} used prompt caching but ${noCacheRates.length === 1 ? 'has' : 'have'} no cache read/write rate, so cached tokens are priced at the full input rate and the cost estimate is too high. Set the cache rates in the Pricing Config tab.`,
  });

  if (summary.by_service.length > 1 && totalTok > 0) {
    const top = summary.by_service[0];
    const pct = Math.round(((top.input_tokens + top.output_tokens) / totalTok) * 100);
    if (pct >= 70) tips.push({ type: 'info',
      title: `${top.service} accounts for ${pct}% of all tokens`,
      description: `One service is driving the majority of AI spend. Consider whether batch sizes or prompt lengths can be reduced.`,
    });
  }

  const snNorm = summary.by_service.find(s => s.service === 'sn-normalization');
  if (snNorm) {
    const haiku  = pricing.find(p => p.model.includes('haiku') && p.is_active);
    const sonnet = pricing.find(p => p.model.includes('sonnet') && p.is_active);
    if (haiku && sonnet && Number(sonnet.input_cost_per_1m) > Number(haiku.input_cost_per_1m)) {
      const sc = estimateCost(snNorm, pricing, sonnet.model);
      const hc = estimateCost(snNorm, pricing, haiku.model);
      if (sc - hc > 0.01) tips.push({ type: 'info',
        title: 'Consider Haiku for software normalization',
        description: `Switching sn-normalization from Sonnet to Haiku could save ~${fmt.usd(sc - hc)} over this period. Haiku handles structured classification at a fraction of the cost.`,
      });
    }
  }

  const { input_tokens: inp, output_tokens: out } = summary.totals;
  if (inp > 0 && out / inp > 0.5) tips.push({ type: 'info',
    title: 'High output-to-input ratio',
    description: `Output tokens are ${Math.round((out/inp)*100)}% of input. Output costs more per token — tighter prompting reduces response length and cost.`,
  });

  if (!tips.length) tips.push({ type: 'success', title: 'Looking good!',
    description: 'No significant cost optimization opportunities detected for the selected time window.' });

  return tips;
}

// ─── Empty state ──────────────────────────────────────────────────────────────
const NoData = () => (
  <Empty description={
    <span>
      No data yet for this period.
      <br />
      <span style={{ fontSize: 12, color: C.muted2 }}>
        Data will appear after AI Chat, Software Normalization, or Rule Synthesis features are used.
      </span>
    </span>
  } />
);

// ─── Main page ────────────────────────────────────────────────────────────────
const AiTokenUsage: React.FC = () => {
  const [accessDenied, setAccessDenied] = useState(false);
  const [days, setDays] = useState(30);

  const [summary, setSummary]           = useState<ITokenSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [logs, setLogs]     = useState<ITokenUsageLog[]>([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logPage, setLogPage]   = useState(1);
  const [logLoading, setLogLoading] = useState(false);
  const [filterService, setFilterService] = useState<string | undefined>();
  const [filterModel,   setFilterModel]   = useState<string | undefined>();

  const [topCalls, setTopCalls]           = useState<ITokenUsageLog[]>([]);
  const [topCallsTotal, setTopCallsTotal] = useState(0);
  const [topCallsPage, setTopCallsPage]   = useState(1);
  const [topCallsLoading, setTopCallsLoading] = useState(false);

  const [pricing, setPricing]           = useState<IModelPricing[]>([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [editRecord, setEditRecord]     = useState<IModelPricing | null>(null);

  const loadSummary = useCallback(async (d: number) => {
    setSummaryLoading(true);
    try { setSummary(await aiTokenUsageService.summary(d)); }
    catch (e: any) { if (e?.response?.status === 403) setAccessDenied(true); }
    finally { setSummaryLoading(false); }
  }, []);

  const loadLogs = useCallback(async (page: number, service?: string, model?: string) => {
    setLogLoading(true);
    try {
      const r = await aiTokenUsageService.list(page, PAGE_SIZE, service, model, 'date');
      setLogs(r.data ?? []); setLogTotal(r.count ?? 0);
    } catch (e: any) { if (e?.response?.status === 403) setAccessDenied(true); }
    finally { setLogLoading(false); }
  }, []);

  const loadTopCalls = useCallback(async (page: number) => {
    setTopCallsLoading(true);
    try {
      const r = await aiTokenUsageService.list(page, PAGE_SIZE, undefined, undefined, 'tokens');
      setTopCalls(r.data ?? []); setTopCallsTotal(r.count ?? 0);
    } catch (e: any) { if (e?.response?.status === 403) setAccessDenied(true); }
    finally { setTopCallsLoading(false); }
  }, []);

  const loadPricing = useCallback(async () => {
    setPricingLoading(true);
    try { const d = await aiTokenUsageService.getPricing(); setPricing(Array.isArray(d) ? d : []); }
    catch { /* non-critical */ }
    finally { setPricingLoading(false); }
  }, []);

  useEffect(() => {
    loadSummary(days); loadLogs(1); loadTopCalls(1); loadPricing();
  }, [loadSummary, loadLogs, loadTopCalls, loadPricing, days]);

  const cost = summary && pricing.length ? totalCost(summary, pricing) : null;
  const tips = summary && pricing.length ? buildTips(summary, pricing) : [];

  // ── Shared column defs ───────────────────────────────────────────────────
  const baseLogCols: ColumnsType<ITokenUsageLog> = [
    { title: 'Executed At', key: 'ts', width: 165,
      render: (_: any, r: ITokenUsageLog) => <Text type="secondary" style={{ fontFamily: MONO, fontSize: 11 }}>{fmt.dt(r.executed_at)}</Text> },
    { title: 'Service',  dataIndex: 'service',       key: 'service',  render: (v: string) => serviceBadge(v) },
    { title: 'Model',    dataIndex: 'model',          key: 'model',    render: (v: string) => <Text code style={{ fontSize: 11 }}>{v}</Text> },
    { title: <Tooltip title="Total input, including cached tokens">Input</Tooltip>, dataIndex: 'input_tokens', key: 'in', align: 'right', width: 100,
      render: (v: number) => <span style={{ fontFamily: MONO, color: C.input }}>{v.toLocaleString()}</span> },
    { title: <Tooltip title="Portion of input read from the prompt cache (billed at the cache-read rate)">Cached</Tooltip>, key: 'cached', align: 'right', width: 100,
      render: (_: any, r: ITokenUsageLog) => <CachedCell t={r} /> },
    { title: 'Output',   dataIndex: 'output_tokens',  key: 'out', align: 'right', width: 110,
      render: (v: number) => <span style={{ fontFamily: MONO, color: C.output }}>{v.toLocaleString()}</span> },
    { title: 'Est. Cost', key: 'cost', align: 'right', width: 100,
      render: (_: any, r: ITokenUsageLog) => {
        const c = estimateCost(r, pricing, r.model);
        return c > 0 ? <span style={{ fontFamily: MONO, color: C.cost }}>{fmt.usd(c)}</span> : <Text type="secondary">—</Text>;
      } },
  ];

  const topCallsCols: ColumnsType<ITokenUsageLog> = [
    ...baseLogCols.filter(c => c.key !== 'cost'),
    { title: 'Total', key: 'total', align: 'right', width: 110,
      render: (_: any, r: ITokenUsageLog) => <Text strong style={{ fontFamily: MONO }}>{fmt.compact(r.input_tokens + r.output_tokens)}</Text> },
    ...baseLogCols.filter(c => c.key === 'cost'),
  ];

  const rateCell = (v: number | null | undefined) =>
    hasRate(v) ? `$${Number(v).toFixed(6)}` : <Tooltip title="Not set — priced at the input rate"><Text type="secondary">—</Text></Tooltip>;

  const pricingCols: ColumnsType<IModelPricing> = [
    { title: 'Model',           dataIndex: 'model',            key: 'model', render: (v: string) => <Text strong>{v}</Text> },
    { title: 'Input / 1M',      dataIndex: 'input_cost_per_1m',  key: 'in',  align: 'right', width: 140, render: (v: number) => `$${Number(v).toFixed(6)}` },
    { title: 'Cache Read / 1M', dataIndex: 'cache_read_cost_per_1m',  key: 'cr', align: 'right', width: 150, render: rateCell },
    { title: 'Cache Write / 1M', dataIndex: 'cache_write_cost_per_1m', key: 'cw', align: 'right', width: 150, render: rateCell },
    { title: 'Output / 1M',     dataIndex: 'output_cost_per_1m', key: 'out', align: 'right', width: 150, render: (v: number) => `$${Number(v).toFixed(6)}` },
    { title: 'Active',          dataIndex: 'is_active',          key: 'act', width: 70,
      render: (v: boolean) => <Text type={v ? 'success' : 'secondary'}>{v ? 'Yes' : 'No'}</Text> },
    { title: 'Updated',         dataIndex: 'updated_at',         key: 'upd', width: 165,
      render: (v: string | null) => v ? <Text type="secondary" style={{ fontSize: 11 }}>{fmt.dt(v)}</Text> : <Text type="secondary">—</Text> },
    { title: '', key: 'edit', width: 50,
      render: (_: any, r: IModelPricing) => (
        <Tooltip title="Edit"><Button type="link" icon={<EditOutlined />} size="small" onClick={() => setEditRecord(r)} /></Tooltip>
      ) },
  ];

  if (accessDenied) {
    return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <Result status="403" title="Access Denied" subTitle="Global Admin role is required." />
    </div>;
  }

  const pagination = (total: number, page: number, onChange: (p: number) => void) =>
    total > PAGE_SIZE ? (
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 0 4px' }}>
        <Text type="secondary">{total.toLocaleString()} total records</Text>
        <Pagination current={page} pageSize={PAGE_SIZE} total={total}
          onChange={onChange} showSizeChanger={false} showQuickJumper />
      </div>
    ) : null;

  return (
    <div style={{ padding: '24px 28px' }}>

      {/* ── Header ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 20, flexWrap:'wrap', gap: 12 }}>
        <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em' }}>AI Token Usage</span>
        <div style={{ display:'flex', gap: 8, alignItems:'center' }}>
          <Select value={days} onChange={v => { setDays(v); loadSummary(v); }} style={{ width: 130 }}>
            <Option value={7}>Last 7 days</Option>
            <Option value={30}>Last 30 days</Option>
            <Option value={90}>Last 90 days</Option>
            <Option value={365}>Last 365 days</Option>
          </Select>
          <Button icon={<ReloadOutlined />}
            onClick={() => { loadSummary(days); loadLogs(logPage, filterService, filterModel); loadTopCalls(topCallsPage); loadPricing(); }}>
            Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultActiveKey="overview">

        {/* ══ Overview ══ */}
        <TabPane tab="Overview" key="overview">
          <Spin spinning={summaryLoading}>
            {summary ? (
              <>
                {/* KPI row — 4 cards */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
                  <Kpi label="Input Tokens"   value={fmt.compact(summary.totals.input_tokens)}   sub={`${summary.totals.input_tokens.toLocaleString()} tokens, incl. cached`}  color={C.input}  />
                  <Kpi label="Cached Input"   value={fmt.compact(cacheRead(summary.totals))}     sub={`${fmtPct(cacheHitPct(summary.totals))} of input read from cache`}   color={C.cache}  />
                  <Kpi label="Output Tokens"  value={fmt.compact(summary.totals.output_tokens)}  sub={`${summary.totals.output_tokens.toLocaleString()} tokens`} color={C.output} />
                  <Kpi label="Est. Cost"      value={cost != null ? fmt.usd(cost) : '—'}         sub="based on configured rates"                                 color={C.cost}   />
                  <Kpi label="AI Calls"       value={summary.totals.call_count.toLocaleString()}  sub={`last ${days} days`}                                       color={C.warn}   />
                </div>

                {/* Row 1: daily chart (left) + model donut (right) */}
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div style={cardPad}>
                    <p style={sectionTitle}>Daily token usage</p>
                    <Text type="secondary" style={{ display:'block', fontSize: 11, marginBottom: 8, marginTop: -8 }}>
                      Tokens paid for each day: what you sent (input, split into uncached, cache write and cache read) and what Claude wrote (output).
                    </Text>
                    {summary.daily.length > 0 ? <DailyChart daily={summary.daily} /> : <NoData />}
                  </div>
                  <div style={cardPad}>
                    <p style={sectionTitle}>Token usage by model</p>
                    <Text type="secondary" style={{ display:'block', fontSize: 11, marginBottom: 8, marginTop: -8 }}>
                      Share of billable tokens per Claude model.
                    </Text>
                    {summary.by_model.length > 0 ? <ModelDonut byModel={summary.by_model} /> : <NoData />}
                  </div>
                </div>

                {/* Row 2: by-service bar (left) + recent calls table (right) */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div style={cardPad}>
                    <p style={sectionTitle}>Tokens by service</p>
                    {summary.by_service.length > 0 ? <ServiceGroupedBar byService={summary.by_service} /> : <NoData />}
                  </div>
                  <div style={cardPad}>
                    <p style={{ ...sectionTitle, display:'flex', alignItems:'center' }}>
                      <span>Recent calls</span>
                      <a href="#" onClick={e => { e.preventDefault(); }} style={{ marginLeft:'auto', fontSize: 12, fontWeight: 400, color: C.input }}>
                        {/* navigation handled by tab click */}
                      </a>
                    </p>
                    {logs.length > 0 ? (
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize: 12 }}>
                        <thead>
                          <tr>
                            {['Time','Service','Input','Cached','Output'].map(h => (
                              <th key={h} style={{ textAlign: ['Input','Cached','Output'].includes(h) ? 'right' : 'left', padding:'6px 8px', borderBottom:`1px solid ${C.border}`, color: C.muted, fontSize: 10, textTransform:'uppercase', letterSpacing:'0.06em', fontWeight: 600 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {logs.slice(0, 8).map(r => (
                            <tr key={r.id} style={{ borderBottom: `1px solid ${C.surface}` }}>
                              <td style={{ padding:'6px 8px', fontFamily: MONO, fontSize: 11, color: C.muted }}>{fmt.dt(r.executed_at)}</td>
                              <td style={{ padding:'6px 8px' }}>{serviceBadge(r.service)}</td>
                              <td style={{ padding:'6px 8px', textAlign:'right', fontFamily: MONO, fontSize: 11, color: C.input }}>{fmt.compact(r.input_tokens)}</td>
                              <td style={{ padding:'6px 8px', textAlign:'right', fontSize: 11 }}><CachedCell t={r} compact /></td>
                              <td style={{ padding:'6px 8px', textAlign:'right', fontFamily: MONO, fontSize: 11, color: C.output }}>{fmt.compact(r.output_tokens)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : <NoData />}
                  </div>
                </div>
              </>
            ) : <NoData />}
          </Spin>
        </TabPane>

        {/* ══ Top Calls ══ */}
        <TabPane tab="Top Calls" key="top-calls">
          <Text type="secondary" style={{ display:'block', marginBottom: 14 }}>
            All-time AI calls sorted by total token count — highest-cost calls first.
          </Text>
          <Spin spinning={topCallsLoading}>
            <Table<ITokenUsageLog> rowKey="id" columns={topCallsCols} dataSource={topCalls}
              pagination={false} locale={{ emptyText: <NoData /> }} />
          </Spin>
          {pagination(topCallsTotal, topCallsPage, p => { setTopCallsPage(p); loadTopCalls(p); })}
        </TabPane>

        {/* ══ By Model ══ */}
        <TabPane tab="By Model" key="by-model">
          <Spin spinning={summaryLoading}>
            {summary && summary.by_model.length > 0 ? (
              <>
                <div style={{ ...cardPad, marginBottom: 16 }}>
                  <p style={sectionTitle}>Token volume by model</p>
                  <ModelBar byModel={summary.by_model} pricing={pricing} />
                </div>
                <Table<ITokenSummary['by_model'][0]> rowKey="model" dataSource={summary.by_model} pagination={false}
                  columns={[
                    { title:'Model',        dataIndex:'model',        key:'model', render:(v:string) => <Text strong>{v}</Text> },
                    { title:'Calls',        dataIndex:'call_count',   key:'calls', align:'right', width:90,  render:(v:number)=><span style={{fontFamily:MONO,color:C.warn}}>{v.toLocaleString()}</span> },
                    { title:'Input',        dataIndex:'input_tokens', key:'in',    align:'right', width:120, render:(v:number)=><span style={{fontFamily:MONO,color:C.input}}>{v.toLocaleString()}</span> },
                    { title:'Cached',       key:'cached',             align:'right', width:120, render:(_:any,r:any)=><CachedCell t={r} /> },
                    { title:'Cache Hit',    key:'hit',                align:'right', width:90,  render:(_:any,r:any)=> cacheRead(r) ? `${cacheHitPct(r).toFixed(0)}%` : <Text type="secondary">—</Text> },
                    { title:'Output',       dataIndex:'output_tokens',key:'out',   align:'right', width:120, render:(v:number)=><span style={{fontFamily:MONO,color:C.output}}>{v.toLocaleString()}</span> },
                    { title:'Total Tokens', key:'total',              align:'right', width:120,   render:(_:any,r:any)=><span style={{fontFamily:MONO}}>{fmt.compact(r.input_tokens+r.output_tokens)}</span> },
                    { title:'Est. Cost',    key:'cost',               align:'right', width:110,
                      render:(_:any,r:any)=>{ const c=estimateCost(r,pricing,r.model); return c>0?<span style={{fontFamily:MONO,color:C.cost}}>{fmt.usd(c)}</span>:<Text type="secondary">—</Text>; } },
                    { title:'% of Total',  key:'pct',                align:'right', width:100,
                      render:(_:any,r:any)=>{ const tot=summary?summary.totals.input_tokens+summary.totals.output_tokens:0; if(!tot) return '—'; return `${((r.input_tokens+r.output_tokens)/tot*100).toFixed(1)}%`; } },
                  ]} />
              </>
            ) : <div style={cardPad}><NoData /></div>}
          </Spin>
        </TabPane>

        {/* ══ By Service ══ */}
        <TabPane tab="By Service" key="by-service">
          <Spin spinning={summaryLoading}>
            {summary && summary.by_service.length > 0 ? (
              <>
                <div style={{ ...cardPad, marginBottom: 16 }}>
                  <p style={sectionTitle}>Token volume by service</p>
                  <ServiceBar byService={summary.by_service} />
                </div>
                <Table<ITokenSummary['by_service'][0]> rowKey="service" dataSource={summary.by_service} pagination={false}
                  columns={[
                    { title:'Service',       dataIndex:'service',       key:'svc',  render:(v:string)=>serviceBadge(v) },
                    { title:'Calls',         dataIndex:'call_count',    key:'calls', align:'right', width:90,  render:(v:number)=><span style={{fontFamily:MONO,color:C.warn}}>{v.toLocaleString()}</span> },
                    { title:'Input',         dataIndex:'input_tokens',  key:'in',   align:'right', width:120, render:(v:number)=><span style={{fontFamily:MONO,color:C.input}}>{v.toLocaleString()}</span> },
                    { title:'Cached',        key:'cached',              align:'right', width:120, render:(_:any,r:any)=><CachedCell t={r} /> },
                    { title:'Cache Hit',     key:'hit',                 align:'right', width:90,  render:(_:any,r:any)=> cacheRead(r) ? `${cacheHitPct(r).toFixed(0)}%` : <Text type="secondary">—</Text> },
                    { title:'Output',        dataIndex:'output_tokens', key:'out',  align:'right', width:120, render:(v:number)=><span style={{fontFamily:MONO,color:C.output}}>{v.toLocaleString()}</span> },
                    { title:'Total Tokens',  key:'total', align:'right', width:120, render:(_:any,r:any)=><span style={{fontFamily:MONO}}>{fmt.compact(r.input_tokens+r.output_tokens)}</span> },
                    { title:'% of Total',    key:'pct',   align:'right', width:100,
                      render:(_:any,r:any)=>{ const tot=summary?summary.totals.input_tokens+summary.totals.output_tokens:0; if(!tot) return '—'; return `${((r.input_tokens+r.output_tokens)/tot*100).toFixed(1)}%`; } },
                  ]} />
              </>
            ) : <div style={cardPad}><NoData /></div>}
          </Spin>
        </TabPane>

        {/* ══ Usage Log ══ */}
        <TabPane tab="Usage Log" key="log">
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
            <Select style={{ width:180 }} placeholder="All services" allowClear value={filterService}
              onChange={v => { setFilterService(v); setLogPage(1); loadLogs(1, v, filterModel); }}>
              {SERVICE_OPTIONS.map(s => <Option key={s} value={s}>{s}</Option>)}
            </Select>
            <Select style={{ width:240 }} placeholder="All models" allowClear value={filterModel}
              onChange={v => { setFilterModel(v); setLogPage(1); loadLogs(1, filterService, v); }}>
              {pricing.map(p => <Option key={p.model} value={p.model}>{p.model}</Option>)}
            </Select>
            <Button icon={<ReloadOutlined />} onClick={() => loadLogs(logPage, filterService, filterModel)} loading={logLoading}>
              Refresh
            </Button>
          </div>
          <Spin spinning={logLoading}>
            <Table<ITokenUsageLog> rowKey="id" columns={baseLogCols} dataSource={logs}
              pagination={false} locale={{ emptyText: <NoData /> }} />
          </Spin>
          {pagination(logTotal, logPage, p => { setLogPage(p); loadLogs(p, filterService, filterModel); })}
        </TabPane>

        {/* ══ Tips ══ */}
        <TabPane tab="Tips" key="tips">
          <Spin spinning={summaryLoading || pricingLoading}>
            {summary && pricing.length > 0 ? (
              <div style={{ maxWidth: 760 }}>
                {tips.map((t, i) => (
                  <Alert key={i} type={t.type} message={t.title} description={t.description} showIcon style={{ marginBottom: 12 }} />
                ))}
              </div>
            ) : (
              <Text type="secondary">{!summary ? 'Loading…' : 'Configure model pricing to see optimization tips.'}</Text>
            )}
          </Spin>
        </TabPane>

        {/* ══ Pricing Config ══ */}
        <TabPane tab="Pricing Config" key="pricing">
          <Spin spinning={pricingLoading}>
            <Table<IModelPricing> rowKey="model" columns={pricingCols} dataSource={pricing}
              pagination={false} locale={{ emptyText: 'No pricing configured. Run the SQL setup script first.' }} />
          </Spin>
          <Text type="secondary" style={{ display:'block', marginTop:12, fontSize:12 }}>
            Prices are in USD per 1,000,000 tokens. Cost estimates are computed at query time and are not stored.
            Cached input is priced at the cache read/write rates; if a cache rate is blank, the input rate is used.
            Calls logged before cache tracking was added count all input at the input rate.
          </Text>
        </TabPane>

      </Tabs>

      <EditPricingModal record={editRecord} onCancel={() => setEditRecord(null)}
        onSuccess={() => { setEditRecord(null); loadPricing(); loadSummary(days); }} />
    </div>
  );
};

export default AiTokenUsage;
